import React from 'react';
import PropTypes from 'prop-types';
import Helmet, { Helmet as Helmet$1 } from 'react-helmet';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var ISO_8601_TIMESPAN = /^P(?!$)(\d+Y)?(\d+M)?(\d+W)?(\d+D)?(T(?=\d+[HMS])(\d+H)?(\d+M)?(\d+S)?)?$/;
var ISO_8601_DATE = /[0-9]{4}-[0-9]{2}-[0-9]{2}/;
var ISO_8601_DATE_OPTIONAL_TIME = /[0-9]{4}-[0-9]{2}-[0-9]{2}(T[0-2][0-9](:[0-5][0-9]){2})?/;

var StructuredDataRecipe = function StructuredDataRecipe(_ref) {
  var image = _ref.image,
      name = _ref.name,
      totalTime = _ref.totalTime,
      datePublished = _ref.datePublished,
      keywords = _ref.keywords,
      calories = _ref.calories,
      steps = _ref.steps;

  if (!image || !name) {
    return null;
  }

  if (totalTime && !ISO_8601_TIMESPAN.test(totalTime)) {
    return new Error("Invalid prop totalTime supplied to StructuredDataRecipe. Validation failed. Please use ISO 8601 ranges");
  }

  if (totalTime && !ISO_8601_DATE.test(datePublished)) {
    return new Error("Invalid prop datePublished supplied to StructuredDataRecipe. Validation failed. Please use ISO 8601 date format");
  }

  var recipeInstructions = steps && steps.length > 0 && steps.map(function (step) {
    return {
      '@type': 'HowToStep',
      'name': step.name,
      'text': step.text
    };
  });
  var data = {
    '@context': 'https://schema.org/',
    '@type': 'Recipe',
    name: name,
    image: image,
    totalTime: totalTime,
    datePublished: datePublished,
    keywords: keywords,
    'nutrition': calories && {
      '@type': 'NutritionInformation',
      calories: calories
    },
    recipeInstructions: recipeInstructions
  };
  return /*#__PURE__*/React.createElement(Helmet, null, /*#__PURE__*/React.createElement("script", {
    type: "application/ld+json"
  }, JSON.stringify(data)));
};

StructuredDataRecipe.propTypes = {
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  name: PropTypes.string.isRequired,
  totalTime: PropTypes.string,
  datePublished: PropTypes.string,
  keywords: PropTypes.string,
  calories: PropTypes.string,
  steps: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    text: PropTypes.string
  }))
};

var StructuredDataBreadcrumb = function StructuredDataBreadcrumb(_ref) {
  var items = _ref.items;

  if (!items || items.length === 0) {
    return null;
  }

  var itemListElement = items.map(function (item, index) {
    return {
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.title,
      'item': item.url
    };
  });
  var data = {
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: itemListElement
  };
  return /*#__PURE__*/React.createElement(Helmet$1, null, /*#__PURE__*/React.createElement("script", {
    type: "application/ld+json"
  }, JSON.stringify(data)));
};

StructuredDataBreadcrumb.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired
  })).isRequired
};

var StructuredDataArticle = function StructuredDataArticle(_ref) {
  var image = _ref.image,
      headline = _ref.headline,
      dateModified = _ref.dateModified,
      datePublished = _ref.datePublished,
      author = _ref.author,
      publisher = _ref.publisher;

  if (dateModified && !ISO_8601_DATE_OPTIONAL_TIME.test(dateModified)) {
    return new Error("Invalid prop dateModified supplied to StructuredDataArticle. Validation failed. Please use ISO 8601 date format");
  }

  if (datePublished && !ISO_8601_DATE_OPTIONAL_TIME.test(datePublished)) {
    return new Error("Invalid prop datePublished supplied to StructuredDataArticle. Validation failed. Please use ISO 8601 date format");
  }

  var data = {
    '@context': 'https://schema.org/',
    '@type': 'NewsArticle',
    headline: headline,
    image: image,
    datePublished: datePublished,
    dateModified: dateModified,
    'author': author ? author.map(function (authorEntry) {
      return {
        '@type': authorEntry['@type'] ? authorEntry['@type'] : 'Person',
        'name': authorEntry.name
      };
    }) : [{
      '@type': 'Organization',
      'name': 'AOK Redaktion'
    }],
    'publisher': {
      '@type': 'Organization',
      'name': publisher === null || publisher === void 0 ? void 0 : publisher.name,
      'logo': publisher === null || publisher === void 0 ? void 0 : publisher.logo
    }
  };
  return /*#__PURE__*/React.createElement(Helmet, null, /*#__PURE__*/React.createElement("script", {
    type: "application/ld+json"
  }, JSON.stringify(data)));
};

StructuredDataArticle.propTypes = {
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  headline: PropTypes.string,
  datePublished: PropTypes.string,
  author: PropTypes.arrayOf(PropTypes.shape({
    '@type': PropTypes.string,
    'name': PropTypes.string.isRequired
  })),
  publisher: PropTypes.shape({
    name: PropTypes.string,
    logo: PropTypes.string
  }),
  dateModified: PropTypes.string
};

var StructuredDataVideo = function StructuredDataVideo(_ref) {
  var name = _ref.name,
      description = _ref.description,
      thumbnailUrl = _ref.thumbnailUrl,
      uploadDate = _ref.uploadDate;

  if (!name || !description || !thumbnailUrl || !uploadDate) {
    return null;
  }

  if (uploadDate && !ISO_8601_DATE_OPTIONAL_TIME.test(uploadDate)) {
    return new Error("Invalid prop uploadDate supplied to StructuredDataVideo. Validation failed. Please use ISO 8601 datetime");
  }

  var data = {
    '@context': 'https://schema.org/',
    '@type': 'VideoObject',
    name: name,
    description: description,
    thumbnailUrl: thumbnailUrl,
    uploadDate: uploadDate
  };
  return /*#__PURE__*/React.createElement(Helmet$1, null, /*#__PURE__*/React.createElement("script", {
    type: "application/ld+json"
  }, JSON.stringify(data)));
};

StructuredDataVideo.propTypes = {
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  thumbnailUrl: PropTypes.string.isRequired,
  uploadDate: PropTypes.string.isRequired
};

var _excluded = ["type"];

var StructuredDataSwitch = function StructuredDataSwitch(_ref) {
  var type = _ref.type,
      rest = _objectWithoutProperties(_ref, _excluded);

  switch (type) {
    case 'recipe':
      return /*#__PURE__*/React.createElement(StructuredDataRecipe, rest);

    case 'breadcrumb':
      return /*#__PURE__*/React.createElement(StructuredDataBreadcrumb, rest);

    case 'article':
      return /*#__PURE__*/React.createElement(StructuredDataArticle, rest);

    case 'video':
      return /*#__PURE__*/React.createElement(StructuredDataVideo, rest);

    default:
      return null;
  }
};

StructuredDataSwitch.propTypes = {
  type: PropTypes.oneOf(['recipe', 'breadcrumb', 'article'])
};

var StructuredDataHowTo = function StructuredDataHowTo(_ref) {
  var name = _ref.name,
      steps = _ref.steps;

  if (!name || !(steps !== null && steps !== void 0 && steps.length)) {
    return null;
  }

  var howToElement = steps.map(function (step) {
    return {
      '@type': 'HowToStep',
      'name': step.name,
      'text': step.text
    };
  });
  var data = {
    '@context': 'http://schema.org',
    '@type': 'HowTo',
    'step': howToElement
  };
  return /*#__PURE__*/React.createElement(Helmet$1, null, /*#__PURE__*/React.createElement("script", {
    type: "application/ld+json"
  }, JSON.stringify(data)));
};

StructuredDataHowTo.propTypes = {
  name: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired
  })).isRequired
};

var StructuredDataFaq = function StructuredDataFaq(_ref) {
  var faqData = _ref.faqData;

  if (!(faqData !== null && faqData !== void 0 && faqData.length)) {
    return null;
  }

  var faqElements = faqData.map(function (faqElement) {
    var _faqElement$answer;

    return {
      '@type': 'Question',
      'name': faqElement === null || faqElement === void 0 ? void 0 : faqElement.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faqElement === null || faqElement === void 0 ? void 0 : (_faqElement$answer = faqElement.answer) === null || _faqElement$answer === void 0 ? void 0 : _faqElement$answer.replace(/<\/?[^>]+(>|$)/g, '')
      }
    };
  });
  return /*#__PURE__*/React.createElement(Helmet$1, null, /*#__PURE__*/React.createElement("script", {
    type: "application/ld+json"
  }, JSON.stringify({
    '@context': 'http://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqElements
  })));
};

StructuredDataFaq.propTypes = {
  faqData: PropTypes.arrayOf(PropTypes.shape({
    question: PropTypes.string,
    answer: PropTypes.string
  }))
};

var StructuredDataQuotation = function StructuredDataQuotation(_ref) {
  var spokenByCharacter = _ref.spokenByCharacter,
      text = _ref.text,
      isPartOf = _ref.isPartOf,
      creator = _ref.creator,
      author = _ref.author;
  var data = {
    '@context': 'https://schema.org/',
    '@type': 'Quotation',
    'spokenByCharacter': spokenByCharacter !== null && spokenByCharacter !== void 0 && spokenByCharacter.name ? {
      '@type': (spokenByCharacter === null || spokenByCharacter === void 0 ? void 0 : spokenByCharacter.type) || 'Person',
      'name': spokenByCharacter === null || spokenByCharacter === void 0 ? void 0 : spokenByCharacter.name
    } : undefined,
    text: text,
    'isPartOf': isPartOf ? {
      '@type': isPartOf === null || isPartOf === void 0 ? void 0 : isPartOf.type,
      'name': isPartOf === null || isPartOf === void 0 ? void 0 : isPartOf.name
    } : undefined,
    'creator': creator !== null && creator !== void 0 && creator.name ? {
      '@type': (creator === null || creator === void 0 ? void 0 : creator.type) || 'Person',
      'name': creator === null || creator === void 0 ? void 0 : creator.name
    } : undefined,
    'author': author ? author.map(function (authorEntry) {
      return {
        '@type': authorEntry['@type'] ? authorEntry['@type'] : 'Person',
        'name': authorEntry.name
      };
    }) : [{
      '@type': 'Organization',
      'name': 'AOK Redaktion'
    }]
  };
  return /*#__PURE__*/React.createElement(Helmet$1, null, /*#__PURE__*/React.createElement("script", {
    type: "application/ld+json"
  }, JSON.stringify(data)));
};

StructuredDataQuotation.propTypes = {
  spokenByCharacter: PropTypes.shape({
    type: PropTypes.string,
    name: PropTypes.string.isRequired
  }),
  text: PropTypes.string.isRequired,
  isPartOf: PropTypes.shape({
    type: PropTypes.string,
    name: PropTypes.string.isRequired
  }),
  creator: PropTypes.shape({
    type: PropTypes.string,
    name: PropTypes.string.isRequired
  }),
  author: PropTypes.arrayOf(PropTypes.shape({
    '@type': PropTypes.string,
    'name': PropTypes.string.isRequired
  }))
};

var StructuredDataWebPage = function StructuredDataWebPage(_ref) {
  var headline = _ref.headline,
      publisher = _ref.publisher,
      datePublished = _ref.datePublished,
      description = _ref.description,
      keywords = _ref.keywords,
      inLanguage = _ref.inLanguage,
      primaryImageOfPage = _ref.primaryImageOfPage;

  if (datePublished && !ISO_8601_DATE_OPTIONAL_TIME.test(datePublished)) {
    return new Error("Invalid prop datePublished supplied to StructuredDataWebPage. Validation failed. Please use ISO 8601 date format");
  }

  if (inLanguage && inLanguage.length !== 2) {
    return new Error("Invalid prop inLanguage supplied to StructuredDataWebPage. Validation failed. Please use ISO 639-1 language code.\n      Visit: https://en.wikipedia.org/wiki/IETF_language_tag#ISO_639-3_and_ISO_639-1 or https://schema.org/WebPage");
  }

  var data = {
    '@context': 'https://schema.org/',
    '@type': 'WebPage',
    headline: headline,
    description: description,
    'publisher': publisher !== null && publisher !== void 0 && publisher.name ? {
      '@type': 'Organization',
      'name': publisher.name
    } : undefined,
    keywords: keywords,
    inLanguage: inLanguage,
    datePublished: datePublished,
    'primaryImageOfPage': primaryImageOfPage ? {
      '@type': 'ImageObject',
      'contentUrl': primaryImageOfPage === null || primaryImageOfPage === void 0 ? void 0 : primaryImageOfPage.contentUrl,
      'copyrightNotice': primaryImageOfPage !== null && primaryImageOfPage !== void 0 && primaryImageOfPage.copyrightNotice ? primaryImageOfPage.copyrightNotice : 'AOK Gesundheitskasse',
      'description': primaryImageOfPage === null || primaryImageOfPage === void 0 ? void 0 : primaryImageOfPage.description,
      'caption': primaryImageOfPage === null || primaryImageOfPage === void 0 ? void 0 : primaryImageOfPage.caption
    } : undefined
  };
  return /*#__PURE__*/React.createElement(Helmet, null, /*#__PURE__*/React.createElement("script", {
    type: "application/ld+json"
  }, JSON.stringify(data)));
};

StructuredDataWebPage.propTypes = {
  headline: PropTypes.string,
  description: PropTypes.string,
  publisher: PropTypes.shape({
    name: PropTypes.string
  }),
  keywords: PropTypes.arrayOf(PropTypes.string),
  inLanguage: PropTypes.string,
  datePublished: PropTypes.string,
  primaryImageOfPage: PropTypes.shape({
    contentUrl: PropTypes.string,
    copyrightNotice: PropTypes.string,
    description: PropTypes.string,
    caption: PropTypes.string
  })
};

var StructuredDataJobPosting = function StructuredDataJobPosting(_ref) {
  var datePosted = _ref.datePosted,
      description = _ref.description,
      hiringOrganization = _ref.hiringOrganization,
      jobLocation = _ref.jobLocation,
      title = _ref.title,
      responsibilities = _ref.responsibilities,
      qualifications = _ref.qualifications,
      validThrough = _ref.validThrough,
      jobLocationType = _ref.jobLocationType,
      jobBenefits = _ref.jobBenefits,
      employmentType = _ref.employmentType,
      employerOverview = _ref.employerOverview,
      applicationContact = _ref.applicationContact,
      jobStartDate = _ref.jobStartDate,
      estimatedSalary = _ref.estimatedSalary;

  if (datePosted && !ISO_8601_DATE_OPTIONAL_TIME.test(validThrough)) {
    return new Error("Invalid prop datePosted supplied to StructuredDataJobPosting. Validation failed. Please use ISO 8601 date format");
  }

  if (validThrough && !ISO_8601_DATE_OPTIONAL_TIME.test(validThrough)) {
    return new Error("Invalid prop validThrough supplied to StructuredDataJobPosting. Validation failed. Please use ISO 8601 date format");
  }

  var data = {
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    datePosted: datePosted,
    description: description,
    'hiringOrganization': {
      '@type': 'Organization',
      'name': hiringOrganization === null || hiringOrganization === void 0 ? void 0 : hiringOrganization.name,
      'logo': hiringOrganization === null || hiringOrganization === void 0 ? void 0 : hiringOrganization.logo
    },
    'jobLocation': jobLocation.map(function (jobLocationEntry) {
      return {
        '@type': 'Place',
        'address': {
          '@type': 'PostalAddress',
          'addressLocality': jobLocationEntry === null || jobLocationEntry === void 0 ? void 0 : jobLocationEntry.addressLocality,
          //city
          'addressRegion': jobLocationEntry === null || jobLocationEntry === void 0 ? void 0 : jobLocationEntry.addressRegion,
          'postalCode': jobLocationEntry === null || jobLocationEntry === void 0 ? void 0 : jobLocationEntry.postalCode,
          'streetAddress': jobLocationEntry === null || jobLocationEntry === void 0 ? void 0 : jobLocationEntry.streetAddress,
          'addressCountry': jobLocationEntry === null || jobLocationEntry === void 0 ? void 0 : jobLocationEntry.addressCountry
        }
      };
    }),
    title: title,
    responsibilities: responsibilities,
    qualifications: qualifications,
    validThrough: validThrough,
    jobLocationType: jobLocationType,
    jobBenefits: jobBenefits,
    employmentType: employmentType,
    employerOverview: employerOverview,
    'applicationContact': applicationContact ? {
      '@type': 'ContactPoint',
      'name': applicationContact === null || applicationContact === void 0 ? void 0 : applicationContact.name,
      'contactType': applicationContact === null || applicationContact === void 0 ? void 0 : applicationContact.contactType,
      'email': applicationContact === null || applicationContact === void 0 ? void 0 : applicationContact.email,
      'faxNumber': applicationContact === null || applicationContact === void 0 ? void 0 : applicationContact.faxNumber,
      'telephone': applicationContact === null || applicationContact === void 0 ? void 0 : applicationContact.telephone,
      'url': applicationContact === null || applicationContact === void 0 ? void 0 : applicationContact.url
    } : undefined,
    jobStartDate: jobStartDate,
    estimatedSalary: estimatedSalary
  };
  return /*#__PURE__*/React.createElement(Helmet, null, /*#__PURE__*/React.createElement("script", {
    type: "application/ld+json"
  }, JSON.stringify(data)));
};

StructuredDataJobPosting.propTypes = {
  datePosted: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  hiringOrganization: PropTypes.shape({
    name: PropTypes.string,
    logo: PropTypes.string
  }),
  jobLocation: PropTypes.arrayOf(PropTypes.shape({
    addressLocality: PropTypes.string,
    addressRegion: PropTypes.string,
    postalCode: PropTypes.string,
    streetAddress: PropTypes.string,
    addressCountry: PropTypes.string
  })).isRequired,
  title: PropTypes.string.isRequired,
  responsibilities: PropTypes.arrayOf(PropTypes.string),
  qualifications: PropTypes.arrayOf(PropTypes.string),
  validThrough: PropTypes.string,
  jobLocationType: PropTypes.string,
  jobBenefits: PropTypes.arrayOf(PropTypes.string),
  employmentType: PropTypes.string,
  employerOverview: PropTypes.string,
  applicationContact: PropTypes.shape({
    name: PropTypes.string,
    contactType: PropTypes.string,
    email: PropTypes.string,
    faxNumber: PropTypes.string,
    telephone: PropTypes.string,
    url: PropTypes.string
  }),
  jobStartDate: PropTypes.string,
  estimatedSalary: PropTypes.string
};
StructuredDataJobPosting.defaultProps = {
  hiringOrganization: {
    name: 'AOK Gesundheitskasse'
  }
};

export { StructuredDataArticle, StructuredDataBreadcrumb, StructuredDataFaq, StructuredDataHowTo, StructuredDataJobPosting, StructuredDataQuotation, StructuredDataRecipe, StructuredDataSwitch, StructuredDataVideo, StructuredDataWebPage };
